import { useCallback, useEffect, useRef, useState } from 'react';
import type { IndiceLexico } from '@core/lexico/indice';
import { INDICE_VAZIO, indiceDeDados } from '@core/lexico/indice';
import { registrarMetrica } from '@instrumentacao/metricas';
import type { RespostaDicionario } from '../../trabalhadores/protocolo';

/**
 * Carrega o dicionário em um worker (§16, §17).
 *
 * Enquanto carrega, o índice é o vazio: a interface bloqueia o campo e mostra
 * `Não foi possível carregar as palavras` + `Tentar novamente` se falhar.
 */

export type EstadoDicionario =
  | { readonly fase: 'carregando' }
  /**
   * Jogável com o recorte de palavras correntes, enquanto o corpus completo
   * ainda chega (§16). A diferença é invisível para quem escreve palavra
   * comum; quem escrever uma rara vê o reconhecimento acontecer quando o
   * resto carrega.
   */
  | { readonly fase: 'parcial'; readonly total: number; readonly duracaoMs: number }
  | { readonly fase: 'pronto'; readonly total: number; readonly duracaoMs: number }
  | { readonly fase: 'erro'; readonly mensagem: string; readonly detalhe?: string };

/** O jogo pode ser jogado? Vale para o índice parcial e para o completo. */
export function dicionarioUtilizavel(estado: EstadoDicionario): boolean {
  return estado.fase === 'parcial' || estado.fase === 'pronto';
}

export interface UsoDicionario {
  readonly estado: EstadoDicionario;
  readonly indice: IndiceLexico;
  readonly tentarNovamente: () => void;
}

export function useDicionario(urlManifesto: string): UsoDicionario {
  const [estado, setEstado] = useState<EstadoDicionario>({ fase: 'carregando' });
  const [indice, setIndice] = useState<IndiceLexico>(INDICE_VAZIO);
  const [tentativa, setTentativa] = useState(0);
  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    let cancelado = false;
    setEstado({ fase: 'carregando' });

    let worker: Worker;
    try {
      worker = new Worker(new URL('../../trabalhadores/dicionario.worker.ts', import.meta.url), {
        type: 'module',
      });
    } catch (erro) {
      setEstado({
        fase: 'erro',
        mensagem: 'Não foi possível carregar as palavras',
        detalhe: erro instanceof Error ? erro.message : String(erro),
      });
      return;
    }

    workerRef.current = worker;

    worker.onmessage = (evento: MessageEvent<RespostaDicionario>) => {
      if (cancelado) return;
      const resposta = evento.data;

      if (resposta.tipo === 'erro') {
        setEstado(
          resposta.detalhe === undefined
            ? { fase: 'erro', mensagem: resposta.mensagem }
            : { fase: 'erro', mensagem: resposta.mensagem, detalhe: resposta.detalhe },
        );
        return;
      }

      registrarMetrica(
        resposta.tipo === 'parcial' ? 'dicionario.parcial.ms' : 'dicionario.carregamento.ms',
        resposta.duracaoMs,
      );
      setIndice(indiceDeDados(resposta.dados));
      setEstado({
        fase: resposta.tipo,
        total: resposta.totalPalavras,
        duracaoMs: resposta.duracaoMs,
      });
    };

    worker.onerror = (evento) => {
      if (cancelado) return;
      setEstado({
        fase: 'erro',
        mensagem: 'Não foi possível carregar as palavras',
        detalhe: evento.message,
      });
    };

    worker.postMessage({ tipo: 'compilar', manifesto: urlManifesto });

    return () => {
      cancelado = true;
      worker.terminate();
      workerRef.current = null;
    };
  }, [urlManifesto, tentativa]);

  const tentarNovamente = useCallback(() => setTentativa((n) => n + 1), []);

  return { estado, indice, tentarNovamente };
}
